/**
 * A110X — the try-on after the phone slept.
 *
 * Reported on iPhone: the page left open, the phone unused for a while, then
 * back to the page: the glasses shake, follow the face late, the frame rate is
 * low, and only a page reload cures it. A sleeping phone can, on its own:
 *  - stop or freeze the camera stream (track ended or muted, frozen video);
 *  - leave the tracking worker hanging, or slow (lost GPU context in it);
 *  - drop the page's WebGL context;
 *  - stall the page for seconds before hiding it (app switcher), which the
 *    quality governor read as a slow device: lower quality, kept for good.
 *
 * The app cures what it can on its own when the page comes back (fresh
 * measurements, the quality it had, fresh tracking) and this watchdog checks,
 * a few times a second, that each part runs again. A part that does not gets
 * restarted once (camera, tracker); if the trouble lasts, the app shows a
 * message with a « Relancer » button (the try-on is restarted in place).
 *
 * Pure logic (no DOM): the app feeds samples, the watchdog answers with
 * actions and whether the message is due.
 */

export type HealthAction = 'restart-camera' | 'restart-tracker';
/** What still goes wrong after the automatic cure: the camera image, the face tracking, the 3D, or the frame rate. */
export type HealthProblem = 'video' | 'tracker' | 'render' | 'slow';
/**
 * A110Y: problems beyond a relaunch, raised by the app itself — the message then asks to refresh
 * the page: `restarts` (automatic restarts capped), `context` (the 3D lost twice, or refused),
 * `memory` (JS heap near its limit, Chrome), `again` (the recover message is back after a relaunch).
 */
export type ReloadProblem = 'restarts' | 'context' | 'memory' | 'again';

export interface HealthSample {
  nowMs: number;
  /** The try-on loop runs and the page is visible (not paused for « Voir le paysage », not closing). */
  active: boolean;
  /** `video.currentTime`: a live camera moves it at every frame, even in front of a still scene. */
  videoTime: number;
  /** The camera track is live and delivering (`readyState === 'live'` and not muted). */
  trackLive: boolean;
  /** Frames offered to the idle tracker so far (accepted or not). */
  framesSent: number;
  /** Tracker results received so far. */
  results: number;
  /** How long the frame in flight has been waiting for its result (0 when idle). */
  trackerBusyMs: number;
  /** The tracker is initialised (false while it restarts). */
  trackerReady: boolean;
  /** The WebGL context is lost. */
  contextLost: boolean;
  /** Current render frame rate. */
  fps: number;
  /** Mean inference time of the steady tracker (0 while it warms up). */
  inferMs: number;
}

export interface HealthParams {
  /** Video time frozen this long → the camera is restarted. */
  videoStallMs: number;
  /** Camera track ended or muted this long → the camera is restarted. */
  trackDeadMs: number;
  /** A frame in flight this long without a result → the tracker is restarted. */
  trackerBusyMs: number;
  /** Frames sent but no result at all this long → the tracker is restarted. */
  noResultMs: number;
  /** WebGL context lost this long → message. */
  contextLostMs: number;
  /** Minimum time between two automatic restarts of the same part. */
  retryAfterMs: number;
  /** A problem still there this long (after the automatic restart) → message. */
  messageAfterMs: number;
  /** After a wake-up: frame rate judged inside this window... */
  slowWindowMs: number;
  /** ...once this settle time has passed (the engine warms up again)... */
  slowSettleMs: number;
  /**
   * ...as too slow under `slowRatio × the frame rate before the sleep` — and
   * under `slowCeilingFps` too (a phone that always ran at 9 fps is not
   * reported for running at 9 fps)...
   */
  slowRatio: number;
  slowCeilingFps: number;
  /** ...for this long. */
  slowForMs: number;
  /** After a wake-up, inference this slow (over `max(slowInferFloorMs, slowInferRatio × before)`)... */
  slowInferRatio: number;
  slowInferFloorMs: number;
  /** ...for this long → the tracker is restarted (the glasses lag behind the face). */
  slowInferForMs: number;
}

export const DEFAULT_HEALTH_PARAMS: HealthParams = {
  videoStallMs: 2500,
  trackDeadMs: 2000,
  trackerBusyMs: 2500,
  noResultMs: 3500,
  contextLostMs: 4000,
  retryAfterMs: 15000,
  messageAfterMs: 4000,
  slowWindowMs: 30000,
  slowSettleMs: 4000,
  slowRatio: 0.45,
  slowCeilingFps: 20,
  slowForMs: 6000,
  slowInferRatio: 2.5,
  slowInferFloorMs: 60,
  slowInferForMs: 3000,
};

export interface HealthVerdict {
  actions: HealthAction[];
  problem: HealthProblem | null;
  /** Show the « Relancer » message now. */
  message: boolean;
}

export class EngineHealth {
  readonly params: HealthParams;
  private wasActive = false;
  private lastVideoTime = Number.NaN;
  private videoChangedAt = 0;
  private trackDeadSince = -1;
  private lastResults = 0;
  private resultAt = 0;
  private framesAtResult = 0;
  private lostSince = -1;
  private cameraRestartAt = -Infinity;
  private trackerRestartAt = -Infinity;
  private problem: HealthProblem | null = null;
  private problemSince = -1;
  private resumeAt = -Infinity;
  private fpsBefore = 0;
  private inferBefore = 0;
  private slowSince = -1;
  private slowInferSince = -1;
  private slowChecks = true;

  constructor(params: Partial<HealthParams> = {}) {
    this.params = { ...DEFAULT_HEALTH_PARAMS, ...params };
  }

  /** The page slept (hidden, or stalled for seconds): timers start afresh, with the frame rate it had before as reference. */
  resumed(nowMs: number, fpsBefore: number, inferBefore: number): void {
    this.resumeAt = nowMs;
    this.fpsBefore = Number.isFinite(fpsBefore) && fpsBefore > 0 ? fpsBefore : 0;
    this.inferBefore = Number.isFinite(inferBefore) && inferBefore > 0 ? inferBefore : 0;
    this.slowInferSince = -1;
    this.videoChangedAt = nowMs;
    this.resultAt = nowMs;
    this.trackDeadSince = -1;
    this.slowSince = -1;
    this.problem = null;
    this.problemSince = -1;
  }

  /** The app restarted the camera (on its own or on the watchdog's word). */
  cameraRestarted(nowMs: number): void {
    this.cameraRestartAt = nowMs;
    this.videoChangedAt = nowMs;
    this.trackDeadSince = -1;
  }

  /** The app restarted the tracker. */
  trackerRestarted(nowMs: number): void {
    this.trackerRestartAt = nowMs;
    this.resultAt = nowMs;
  }

  /** After a restart asked by the customer: a slow device is not reported again (it is just slow). */
  stopSlowChecks(): void {
    this.slowChecks = false;
    this.slowSince = -1;
  }

  observe(s: HealthSample): HealthVerdict {
    const p = this.params;
    const now = s.nowMs;
    if (!s.active) {
      // Hidden, paused or closing: nothing is judged, and the clocks restart on return.
      this.wasActive = false;
      this.problem = null;
      this.problemSince = -1;
      return { actions: [], problem: null, message: false };
    }
    if (!this.wasActive) {
      this.wasActive = true;
      this.videoChangedAt = now;
      this.lastVideoTime = s.videoTime;
      this.resultAt = now;
      this.lastResults = s.results;
      this.framesAtResult = s.framesSent;
      this.trackDeadSince = -1;
      this.lostSince = -1;
      this.slowSince = -1;
    }

    if (s.videoTime !== this.lastVideoTime) {
      this.lastVideoTime = s.videoTime;
      this.videoChangedAt = now;
    }
    if (s.trackLive) this.trackDeadSince = -1;
    else if (this.trackDeadSince < 0) this.trackDeadSince = now;

    if (s.results !== this.lastResults) {
      this.lastResults = s.results;
      this.resultAt = now;
      this.framesAtResult = s.framesSent;
    }
    if (!s.trackerReady) {
      // Restarting: not judged until it answers again.
      this.resultAt = now;
      this.framesAtResult = s.framesSent;
    }

    if (s.contextLost) {
      if (this.lostSince < 0) this.lostSince = now;
    } else this.lostSince = -1;

    const actions: HealthAction[] = [];
    let problem: HealthProblem | null = null;

    const videoStuck = now - this.videoChangedAt > p.videoStallMs || (this.trackDeadSince >= 0 && now - this.trackDeadSince > p.trackDeadMs);
    if (videoStuck) {
      if (now - this.cameraRestartAt > p.retryAfterMs) {
        actions.push('restart-camera');
        this.cameraRestarted(now);
      } else problem = 'video';
    }

    // A frozen camera starves the tracker: only judged on a moving image.
    const trackerStuck = !videoStuck && s.trackerReady && (s.trackerBusyMs > p.trackerBusyMs || (now - this.resultAt > p.noResultMs && s.framesSent > this.framesAtResult));
    if (trackerStuck) {
      if (now - this.trackerRestartAt > p.retryAfterMs) {
        actions.push('restart-tracker');
        this.trackerRestarted(now);
      } else problem ??= 'tracker';
    }

    if (this.lostSince >= 0 && now - this.lostSince > p.contextLostMs) problem ??= 'render';

    const sinceResume = now - this.resumeAt;
    // Back from a sleep, the tracker answers but slowly: the glasses lag behind the face.
    if (!trackerStuck && s.trackerReady && this.inferBefore > 0 && s.inferMs > 0 && sinceResume < p.slowWindowMs) {
      if (s.inferMs > Math.max(p.slowInferFloorMs, this.inferBefore * p.slowInferRatio)) {
        if (this.slowInferSince < 0) this.slowInferSince = now;
        if (now - this.slowInferSince > p.slowInferForMs) {
          if (now - this.trackerRestartAt > p.retryAfterMs) {
            actions.push('restart-tracker');
            this.trackerRestarted(now);
            this.slowInferSince = -1;
          } else problem ??= 'tracker';
        }
      } else this.slowInferSince = -1;
    } else this.slowInferSince = -1;

    if (this.slowChecks && this.fpsBefore > 0 && sinceResume >= p.slowSettleMs && sinceResume < p.slowWindowMs) {
      const slow = s.fps > 0 && s.fps < this.fpsBefore * p.slowRatio && s.fps < p.slowCeilingFps;
      if (slow) {
        if (this.slowSince < 0) this.slowSince = now;
        if (now - this.slowSince > p.slowForMs) problem ??= 'slow';
      } else this.slowSince = -1;
    } else this.slowSince = -1;

    if (problem === null) this.problemSince = -1;
    else if (this.problem === null || this.problemSince < 0) this.problemSince = now;
    this.problem = problem;
    // A slow frame rate has already been judged over `slowForMs`: no extra wait.
    const message = problem !== null && (problem === 'slow' || now - this.problemSince >= p.messageAfterMs);
    return { actions, problem, message };
  }
}

/**
 * Quality levels over time, to give back after a sleep the level the try-on
 * had before the stall that preceded it (a stalled page reads as a slow device
 * and steps the quality down; it is not one).
 */
export class LevelHistory {
  private readonly changes: Array<{ t: number; level: number }> = [];

  constructor(private readonly initialLevel: number) {}

  record(tMs: number, level: number): void {
    this.changes.push({ t: tMs, level });
    // Ten minutes are plenty; the oldest entries only matter for their level.
    while (this.changes.length > 64) this.changes.shift();
  }

  /** Level in effect at `tMs`. */
  levelAt(tMs: number): number {
    let level = this.initialLevel;
    for (const c of this.changes) {
      if (c.t > tMs) break;
      level = c.level;
    }
    return level;
  }
}
